import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users as UsersIcon, Loader2, Settings } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth, Card, Button, Badge, appService, appSeatService, AppSeat } from '@aaas/shared';
import { useWorkspaceMembers } from '../../hooks/useWorkspaceMembers';

interface AppWithSeats {
  appId: string;
  appName: string;
  appSlug: string;
  appIsBeta: boolean;
  memberCount: number;
  members: Array<{
    id: string;
    email: string;
    displayName: string | null;
  }>;
}

export function AppSeatsOverview() {
  const { currentWorkspace, user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation('portal');
  const [appsWithSeats, setAppsWithSeats] = useState<AppWithSeats[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch workspace members
  const { data: members, isLoading: membersLoading } = useWorkspaceMembers(currentWorkspace?.id);

  useEffect(() => {
    if (currentWorkspace && members && user) {
      loadSeatsData();
    }
  }, [currentWorkspace, members, user]);

  const loadSeatsData = async () => {
    if (!members || !currentWorkspace || !user) return;

    setLoading(true);
    try {
      const idToken = await user.getIdToken();

      // Fetch apps subscribed by this workspace
      const subscriptions = await appService.getWorkspaceSubscriptions(
        currentWorkspace.id,
        idToken
      );

      // Fetch seats for each subscribed app
      const appsData: AppWithSeats[] = [];
      for (const app of subscriptions) {
        try {
          // Fetch seat assignments for this app
          const seatsResponse = await appSeatService.getAppSeats(
            currentWorkspace.id,
            app.app_id,
            idToken
          );

          // Map seats to members
          const assignedMembers = seatsResponse.seats
            .map((seat: AppSeat) => {
              const member = members.find((m) => m.account_id === seat.account_id);
              return member
                ? {
                    id: member.account_id,
                    email: member.email || '',
                    displayName: member.display_name,
                  }
                : null;
            })
            .filter((member): member is { id: string; email: string; displayName: string | null } =>
              member !== null
            );

          appsData.push({
            appId: app.app_id,
            appName: app.app_name,
            appSlug: app.app_slug,
            appIsBeta: app.app_is_beta,
            memberCount: assignedMembers.length,
            members: assignedMembers,
          });
        } catch (error) {
          // If fetching seats fails for an app, log and continue
          console.warn(`Failed to fetch seats for app ${app.app_slug}:`, error);
        }
      }

      setAppsWithSeats(appsData);
    } catch (error) {
      console.error('Failed to load seat assignments:', error);
    } finally {
      setLoading(false);
    }
  };

  if (membersLoading || loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (appsWithSeats.length === 0) {
    return (
      <Card className="p-8 text-center">
        <p className="text-gray-600">{t('seatsOverview.noApps')}</p>
        <p className="text-sm text-gray-500 mt-2">{t('seatsOverview.noMarketplace')}</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">{t('seatsOverview.heading')}</h2>
        <p className="text-gray-600">{t('seatsOverview.subheading')}</p>
      </div>

      <div className="space-y-4">
        {appsWithSeats.map((app) => (
          <Card key={app.appId} className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-lg font-semibold text-gray-900">{app.appName}</h3>
                  <Badge variant="secondary" className="text-xs">
                    {app.appSlug}
                  </Badge>
                  {app.appIsBeta && (
                    <Badge variant="outline" className="text-orange-600 border-orange-200 text-xs">
                      {t('betaTag', { ns: 'portal', defaultValue: 'Beta' })}
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <UsersIcon className="w-4 h-4" />
                  <span>{t('seatsOverview.membersAssigned', { count: app.memberCount })}</span>
                </div>
              </div>
              <Button
                onClick={() => navigate(`/workspace/assign-seats?app=${app.appId}`)}
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
              >
                <Settings className="w-4 h-4" />
                {t('seatsOverview.manage')}
              </Button>
            </div>

            {app.members.length > 0 ? (
              <div className="mt-4 pt-4 border-t">
                <p className="text-sm font-medium text-gray-700 mb-2">{t('seatsOverview.assignedMembers')}</p>
                <div className="flex flex-wrap gap-2">
                  {app.members.map((member) => (
                    <div
                      key={member.id}
                      className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-full"
                    >
                      <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center">
                        <span className="text-blue-600 font-medium text-xs">
                          {member.displayName?.charAt(0).toUpperCase() ||
                            member.email?.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <span className="text-sm text-gray-700">
                        {member.displayName || member.email}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="mt-4 pt-4 border-t">
                <p className="text-sm text-gray-500 italic">{t('seatsOverview.noneAssigned')}</p>
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}
